import type { ReactNode } from 'react'
import { Box, Flex, Select, Text, TextField } from '@radix-ui/themes'
import type { Ops } from '@willie/schemas'
import { loggerMakerLabel } from '~/features/installations/constants'
import {
  SHAYP_BUILDING_TYPE_GROUPS,
  SUPPORTED_DATA_SOURCE_MAKERS,
  defaultDataSourceValues,
  type DataSourceMaker,
  type MeterDataSourceFieldValues
} from '~/features/installations/dataSource'

const EFENTO_CHANNELS = ['1', '2', '3', '4', '5']

interface MeterDataSourceFieldsProps {
  values: MeterDataSourceFieldValues
  onChange: (values: MeterDataSourceFieldValues) => void
}

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box flexGrow="1">
    <Text as="label" size="1" color="gray" className="block mb-1">
      {label}
    </Text>
    {children}
  </Box>
)

const ChannelAndPulseWeightFields = ({
  channelValue,
  channelOptions,
  onChannelChange,
  pulseWeight,
  onPulseWeightChange
}: {
  channelValue: string
  channelOptions: readonly string[]
  onChannelChange: (value: string) => void
  pulseWeight: string
  onPulseWeightChange: (value: string) => void
}) => (
  <Flex gap="2" align="end">
    <Field label="Channel">
      <Select.Root value={channelValue} onValueChange={onChannelChange}>
        <Select.Trigger className="w-full" />
        <Select.Content>
          {channelOptions.map((channel) => (
            <Select.Item key={channel} value={channel}>
              {channel}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </Field>
    <Field label="Pulse weight">
      <TextField.Root
        type="number"
        step="any"
        value={pulseWeight}
        onChange={(e) => onPulseWeightChange(e.target.value)}
      >
        <TextField.Slot side="right">
          <Text size="2" className="text-gray-11">
            L
          </Text>
        </TextField.Slot>
      </TextField.Root>
    </Field>
  </Flex>
)

export const MeterDataSourceFields = ({ values, onChange }: MeterDataSourceFieldsProps) => {
  const update = (patch: Partial<MeterDataSourceFieldValues>) => onChange({ ...values, ...patch })

  return (
    <Flex direction="column" gap="3">
      <Flex gap="2" align="end">
        <Field label="Logger maker">
          <Select.Root
            value={values.maker}
            onValueChange={(maker) =>
              // Reset maker-specific defaults (pulse weight) when switching maker, keeping the
              // serial and the Shayp device description.
              onChange(
                defaultDataSourceValues(maker as DataSourceMaker, values.serial, undefined, values.description)
              )
            }
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              {SUPPORTED_DATA_SOURCE_MAKERS.map((maker) => (
                <Select.Item key={maker} value={maker}>
                  {loggerMakerLabel[maker]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Field>
        <Field label={values.maker === 'shayp' ? 'Serial number' : 'Device ID'}>
          <TextField.Root
            value={values.serial}
            onChange={(e) => update({ serial: e.target.value })}
            className="font-mono"
          />
        </Field>
      </Flex>

      {values.maker === 'shayp' ? (
        <>
          <Field label="Building type">
            <Select.Root
              value={values.buildingType}
              onValueChange={(buildingType) =>
                update({ buildingType: buildingType as Ops.StationInstallations.ShaypBuildingType })
              }
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                {SHAYP_BUILDING_TYPE_GROUPS.map((group) => (
                  <Select.Group key={group.label}>
                    <Select.Label>{group.label}</Select.Label>
                    {group.options.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Group>
                ))}
              </Select.Content>
            </Select.Root>
          </Field>
          <Field label="Pulse weight">
            <TextField.Root
              type="number"
              step="any"
              value={values.pulseWeight}
              onChange={(e) => update({ pulseWeight: e.target.value })}
            >
              <TextField.Slot side="right">
                <Text size="2" className="text-gray-11">
                  L
                </Text>
              </TextField.Slot>
            </TextField.Root>
          </Field>
          <Field label="Device description">
            <TextField.Root
              value={values.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Org_Site_Station"
            />
          </Field>
          <Text size="1" color="gray">
            We'll register this device in Shayp from the serial, then link it to the station.
          </Text>
        </>
      ) : null}

      {values.maker === 'adeunis' ? (
        <ChannelAndPulseWeightFields
          channelValue={values.channelAB}
          channelOptions={['A', 'B']}
          onChannelChange={(channelAB) => update({ channelAB: channelAB as 'A' | 'B' })}
          pulseWeight={values.pulseWeight}
          onPulseWeightChange={(pulseWeight) => update({ pulseWeight })}
        />
      ) : null}

      {values.maker === 'efento' ? (
        <ChannelAndPulseWeightFields
          channelValue={values.channelNum}
          channelOptions={EFENTO_CHANNELS}
          onChannelChange={(channelNum) => update({ channelNum })}
          pulseWeight={values.pulseWeight}
          onPulseWeightChange={(pulseWeight) => update({ pulseWeight })}
        />
      ) : null}
    </Flex>
  )
}
